import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, X, Trash2, MapPin, Search, Loader2, Layout, Check, Sparkles, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { propertyService, aiService } from "../services/api";
import { toast } from "sonner";
import axios from "axios";

// Helper function to build image URLs with proper encoding
const getImageUrl = (imagePath, { width = 800, quality = 'auto:low' } = {}) => {
  if (!imagePath) return null;
  
  if (imagePath.includes('res.cloudinary.com') && imagePath.includes('/upload/')) {
    const transform = `f_webp,q_${quality},w_${width},c_limit`;
    return imagePath.replace('/upload/', `/upload/${transform}/`);
  }
  
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  
  let normalizedPath = imagePath;
  if (imagePath.startsWith('/uploads/')) {
    normalizedPath = '/api' + imagePath;
  }
  
  const segments = normalizedPath.split('/');
  const encodedSegments = segments.map((segment) => {
    if (segment === '' || segment === 'api' || segment === 'uploads' || segment === 'properties') {
      return segment;
    }
    return encodeURIComponent(segment);
  });
  return `${baseUrl}${encodedSegments.join('/')}`;
};

// Debounce hook for address search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

export const PropertyForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromSuperAdmin = location.state?.from === 'super-admin';
  const { id } = useParams();
  const isEdit = !!id;
  
  // Check if current user is admin
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";
  const userPlan = user?.plan || "basico";
  const isProPlus = ["pro", "premium", "agencia"].includes(userPlan) || isAdmin;

  // AI generation state
  const [aiLoading, setAiLoading] = useState({ description: false, titles: false, whatsapp: false });
  const [titleSuggestions, setTitleSuggestions] = useState(null);
  const [aiUpsellOpen, setAiUpsellOpen] = useState(false);

  const buildAiPayload = () => ({
    title: formData.title || null,
    property_type: formData.property_type || null,
    operation_type: formData.operation_type || null,
    price: formData.price ? parseFloat(formData.price) : null,
    location: formData.location || null,
    specs: formData.specs,
    amenities: (formData.amenities || []).map(a => ({ name: a.label || a.name || a }))
  });

  const handleAiDescription = async () => {
    if (!isProPlus) { setAiUpsellOpen(true); return; }
    setAiLoading(s => ({ ...s, description: true }));
    try {
      const text = await aiService.generateDescription(buildAiPayload());
      setFormData(f => ({ ...f, description: text }));
      toast.success("Descripción generada con IA");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar la descripción");
    } finally {
      setAiLoading(s => ({ ...s, description: false }));
    }
  };

  const handleAiTitles = async () => {
    if (!isProPlus) { setAiUpsellOpen(true); return; }
    setAiLoading(s => ({ ...s, titles: true }));
    try {
      const titles = await aiService.generateTitles(buildAiPayload());
      setTitleSuggestions(titles);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron generar títulos");
    } finally {
      setAiLoading(s => ({ ...s, titles: false }));
    }
  };

  const handleAiWhatsapp = async () => {
    if (!isProPlus) { setAiUpsellOpen(true); return; }
    setAiLoading(s => ({ ...s, whatsapp: true }));
    try {
      const text = await aiService.generateWhatsApp(buildAiPayload());
      setFormData(f => ({ ...f, whatsapp_message: text }));
      toast.success("Mensaje generado con IA");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo generar el mensaje");
    } finally {
      setAiLoading(s => ({ ...s, whatsapp: false }));
    }
  };

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedMainImage, setSelectedMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreview, setImagePreview] = useState([]);
  
  // Address search state
  const [addressSearch, setAddressSearch] = useState("");
  const [addressResults, setAddressResults] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [showAddressResults, setShowAddressResults] = useState(false);
  const debouncedSearch = useDebounce(addressSearch, 500);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    property_type: "casa",
    operation_type: "venta",
    status: "active",
    location: "",
    address: {
      street: "",
      exterior_number: "",
      interior_number: "",
      postal_code: "",
      neighborhood: "",
      city: "",
      state: ""
    },
    latitude: "",
    longitude: "",
    specs: {
      bedrooms: "",
      bathrooms: "",
      half_bathrooms: "",
      parking: "",
      square_meters: "",
      construction_meters: "",
      tv_room: false,
      laundry_room: false,
      service_room: false,
      roof_garden: false,
      balcony: false,
      terrace: false
    },
    amenities: [],
    video_url: "",
    whatsapp_number: "523332509484",
    contact_email: "",
    contact_method: "both",
    whatsapp_message: "",
    form_title: "",
    form_description: "",
    custom_slug: "",
    primary_color: "#a033fd",
    secondary_color: "#ff1758",
    template: "clasica",
    facebook_pixel_id: "",
    google_analytics_id: "",
    google_ads_id: "",
    tiktok_pixel_id: ""
  });

  const [currentProperty, setCurrentProperty] = useState(null);
  const [newAmenity, setNewAmenity] = useState("");

  // Predefined color options
  const colorPresets = [
    { name: "Púrpura", primary: "#a033fd", secondary: "#ff1758" },
    { name: "Azul", primary: "#2563eb", secondary: "#3b82f6" },
    { name: "Verde", primary: "#059669", secondary: "#10b981" },
    { name: "Rojo", primary: "#dc2626", secondary: "#ef4444" },
    { name: "Naranja", primary: "#ea580c", secondary: "#f97316" },
    { name: "Rosa", primary: "#db2777", secondary: "#ec4899" },
    { name: "Negro", primary: "#171717", secondary: "#404040" },
    { name: "Dorado", primary: "#b45309", secondary: "#d97706" },
  ];

  useEffect(() => {
    if (isEdit) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    try {
      // For admin users, use admin endpoint to see any property
      let data;
      if (isAdmin) {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/properties/${id}`);
        data = response.data;
      } else {
        data = await propertyService.getById(id);
      }
      setCurrentProperty(data);
      
      const addressData = data.address || {};
      const specsData = data.specs || {};
      
      setFormData({
        title: data.title || "",
        description: data.description || "",
        price: String(data.price || ""),
        property_type: data.property_type || "casa",
        operation_type: data.operation_type || "venta",
        status: data.status || "active",
        location: data.location || "",
        address: {
          street: addressData.street || "",
          exterior_number: addressData.exterior_number || "",
          interior_number: addressData.interior_number || "",
          postal_code: addressData.postal_code || "",
          neighborhood: addressData.neighborhood || "",
          city: addressData.city || "",
          state: addressData.state || ""
        },
        latitude: String(data.latitude || ""),
        longitude: String(data.longitude || ""),
        specs: {
          bedrooms: String(specsData.bedrooms || ""),
          bathrooms: String(specsData.bathrooms || ""),
          half_bathrooms: String(specsData.half_bathrooms || ""),
          parking: String(specsData.parking || ""),
          square_meters: String(specsData.square_meters || ""),
          construction_meters: String(specsData.construction_meters || ""),
          tv_room: specsData.tv_room || false,
          laundry_room: specsData.laundry_room || false,
          service_room: specsData.service_room || false,
          roof_garden: specsData.roof_garden || false,
          balcony: specsData.balcony || false,
          terrace: specsData.terrace || false
        },
        amenities: data.amenities || [],
        video_url: data.video_url || "",
        whatsapp_number: data.whatsapp_number || "523332509484",
        contact_email: data.contact_email || "",
        contact_method: data.contact_method || "both",
        whatsapp_message: data.whatsapp_message || "",
        form_title: data.form_title || "",
        form_description: data.form_description || "",
        custom_slug: data.slug || "",
        primary_color: data.primary_color || "#a033fd",
        secondary_color: data.secondary_color || "#ff1758",
        template: data.template || "clasica",
        facebook_pixel_id: data.facebook_pixel_id || "",
        google_analytics_id: data.google_analytics_id || "",
        google_ads_id: data.google_ads_id || "",
        tiktok_pixel_id: data.tiktok_pixel_id || ""
      });
    } catch (error) {
      console.error("Error loading property:", error);
      toast.error("Error al cargar propiedad");
    }
  };

  // Search addresses using Nominatim (OpenStreetMap)
  useEffect(() => {
    const searchAddresses = async () => {
      if (!debouncedSearch || debouncedSearch.length < 3) {
        setAddressResults([]);
        return;
      }
      
      setSearchingAddress(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(debouncedSearch)}&countrycodes=mx&limit=5&addressdetails=1`,
          {
            headers: {
              'Accept-Language': 'es'
            }
          }
        );
        const data = await response.json();
        setAddressResults(data);
        setShowAddressResults(true);
      } catch (error) {
        console.error("Error searching address:", error);
      } finally {
        setSearchingAddress(false);
      }
    };
    
    searchAddresses();
  }, [debouncedSearch]);

  // Select an address from search results
  const handleSelectAddress = (result) => {
    const addr = result.address || {};
    
    setFormData(prev => ({
      ...prev,
      location: result.display_name.split(',').slice(0, 3).join(','),
      latitude: result.lat,
      longitude: result.lon,
      address: {
        ...prev.address,
        street: addr.road || addr.street || "",
        exterior_number: addr.house_number || "",
        neighborhood: addr.suburb || addr.neighbourhood || addr.quarter || "",
        city: addr.city || addr.town || addr.municipality || "",
        state: addr.state || "",
        postal_code: addr.postcode || ""
      }
    }));
    
    setAddressSearch("");
    setShowAddressResults(false);
    setAddressResults([]);
    toast.success("Ubicación seleccionada - las coordenadas se agregaron automáticamente");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("La descripción es requerida");
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("El precio debe ser mayor a 0");
      return;
    }
    if (!formData.location.trim()) {
      toast.error("La ubicación es requerida");
      return;
    }
    if (!formData.whatsapp_number.trim()) {
      toast.error("El número de WhatsApp es requerido");
      return;
    }
    
    setLoading(true);

    try {
      const propertyData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        property_type: formData.property_type,
        operation_type: formData.operation_type,
        status: formData.status,
        location: formData.location,
        address: {
          street: formData.address.street || null,
          exterior_number: formData.address.exterior_number || null,
          interior_number: formData.address.interior_number || null,
          postal_code: formData.address.postal_code || null,
          neighborhood: formData.address.neighborhood || null,
          city: formData.address.city || null,
          state: formData.address.state || null
        },
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        specs: {
          bedrooms: formData.specs.bedrooms ? parseInt(formData.specs.bedrooms) : null,
          bathrooms: formData.specs.bathrooms ? parseInt(formData.specs.bathrooms) : null,
          half_bathrooms: formData.specs.half_bathrooms ? parseInt(formData.specs.half_bathrooms) : null,
          parking: formData.specs.parking ? parseInt(formData.specs.parking) : null,
          square_meters: formData.specs.square_meters ? parseFloat(formData.specs.square_meters) : null,
          construction_meters: formData.specs.construction_meters ? parseFloat(formData.specs.construction_meters) : null,
          tv_room: formData.specs.tv_room,
          laundry_room: formData.specs.laundry_room,
          service_room: formData.specs.service_room,
          roof_garden: formData.specs.roof_garden,
          balcony: formData.specs.balcony,
          terrace: formData.specs.terrace
        },
        amenities: formData.amenities,
        video_url: formData.video_url || null,
        whatsapp_number: formData.whatsapp_number,
        contact_email: formData.contact_email || null,
        contact_method: formData.contact_method,
        whatsapp_message: formData.whatsapp_message?.trim() || null,
        form_title: formData.form_title?.trim() || null,
        form_description: formData.form_description?.trim() || null,
        custom_slug: formData.custom_slug || null,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        template: formData.template || "clasica",
        facebook_pixel_id: formData.facebook_pixel_id || null,
        google_analytics_id: formData.google_analytics_id || null,
        google_ads_id: formData.google_ads_id || null,
        tiktok_pixel_id: formData.tiktok_pixel_id || null
      };

      if (isEdit) {
        // Use admin endpoint if user is admin, otherwise regular endpoint
        if (isAdmin) {
          await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/properties/${id}`, propertyData);
        } else {
          await propertyService.update(id, propertyData);
        }
        toast.success("✅ Propiedad actualizada exitosamente");
        setLoading(false);
        navigate(cameFromSuperAdmin ? "/super-admin" : "/admin/dashboard");
      } else {
        const newProperty = await propertyService.create(propertyData);
        toast.success("✅ Propiedad creada");
        
        let uploadErrors = [];
        
        // Upload main image if selected
        if (selectedMainImage) {
          toast.info("Subiendo imagen principal...");
          try {
            await propertyService.uploadMainImage(newProperty.id, selectedMainImage);
            toast.success("✅ Imagen principal subida");
          } catch (imgError) {
            console.error("Error uploading main image:", imgError);
            uploadErrors.push("imagen principal");
          }
        }
        
        // Upload gallery images if any were selected
        if (selectedImages.length > 0) {
          toast.info("Subiendo galería de imágenes...");
          try {
            await propertyService.uploadImages(newProperty.id, selectedImages);
            toast.success("✅ Galería subida");
          } catch (imgError) {
            console.error("Error uploading images:", imgError);
            uploadErrors.push("galería");
          }
        }
        
        setLoading(false);
        
        if (uploadErrors.length > 0) {
          toast.error(`Error al subir: ${uploadErrors.join(", ")}. Puedes subirlas editando la propiedad.`);
        }
        
        // Navigate to edit page to add/fix images
        navigate(`/admin/properties/edit/${newProperty.id}`);
      }
    } catch (error) {
      console.error("Error saving property:", error);
      const errorMessage = error.response?.data?.detail || "Error al guardar propiedad";
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleMainImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedMainImage(file);
    const preview = URL.createObjectURL(file);
    setMainImagePreview(preview);
    
    toast.success("Imagen principal seleccionada");
  };

  const removeMainImage = () => {
    if (mainImagePreview) {
      URL.revokeObjectURL(mainImagePreview);
    }
    setSelectedMainImage(null);
    setMainImagePreview(null);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Limit to 10 images
    if (files.length > 10) {
      toast.error("Máximo 10 imágenes para la galería");
      return;
    }

    setSelectedImages(files);

    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreview(previews);
    
    toast.success(`${files.length} imagen(es) seleccionada(s)`);
  };

  const removeSelectedImage = (index) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreview.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(imagePreview[index]);
    
    setSelectedImages(newImages);
    setImagePreview(newPreviews);
  };

  const handleDeleteMainImage = async () => {
    if (!window.confirm("¿Eliminar imagen principal?")) return;

    try {
      await propertyService.deleteMainImage(id);
      toast.success("Imagen principal eliminada");
      loadProperty();
    } catch (error) {
      toast.error("Error al eliminar imagen principal");
    }
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImages(true);
    try {
      await propertyService.uploadMainImage(id, file);
      toast.success("Imagen principal actualizada");
      loadProperty();
    } catch (error) {
      toast.error("Error al subir imagen principal");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check current count + new files
    const currentCount = currentProperty?.images?.length || 0;
    if (currentCount + files.length > 10) {
      toast.error(`Máximo 10 imágenes. Tienes ${currentCount}, puedes agregar ${10 - currentCount} más`);
      return;
    }

    setUploadingImages(true);
    try {
      await propertyService.uploadImages(id, files);
      toast.success("Imágenes subidas");
      loadProperty();
    } catch (error) {
      toast.error("Error al subir imágenes");
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm("¿Eliminar esta imagen?")) return;

    try {
      await propertyService.deleteImage(id, imageUrl);
      toast.success("Imagen eliminada");
      loadProperty();
    } catch (error) {
      toast.error("Error al eliminar imagen");
    }
  };

  const addAmenity = () => {
    if (!newAmenity.trim()) return;
    setFormData({
      ...formData,
      amenities: [...formData.amenities, { name: newAmenity, icon: null }]
    });
    setNewAmenity("");
  };

  const removeAmenity = (index) => {
    setFormData({
      ...formData,
      amenities: formData.amenities.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? "Editar Propiedad" : "Nueva Propiedad"}
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Información Básica</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="title">Título *</Label>
                    <button
                      type="button"
                      onClick={handleAiTitles}
                      disabled={aiLoading.titles}
                      className="text-xs flex items-center gap-1 text-brand-purple hover:text-brand-pink disabled:opacity-50 font-medium"
                      data-testid="ai-titles-btn"
                    >
                      {aiLoading.titles ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isProPlus ? <Sparkles className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />)}
                      {aiLoading.titles ? "Generando..." : "Sugerencias con IA"}
                    </button>
                  </div>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Casa moderna en Guadalajara"
                    required
                    data-testid="property-title-input"
                  />
                  {titleSuggestions && titleSuggestions.length > 0 && (
                    <div className="mt-3 p-3 rounded-lg bg-purple-50/60 border border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-brand-purple flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5" /> Sugerencias de IA
                        </p>
                        <button type="button" onClick={() => setTitleSuggestions(null)} className="text-xs text-gray-500 hover:text-gray-700">Cerrar</button>
                      </div>
                      <div className="space-y-1.5">
                        {titleSuggestions.map((t, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => { setFormData({ ...formData, title: t }); setTitleSuggestions(null); toast.success("Título aplicado"); }}
                            className="w-full text-left text-sm px-3 py-2 rounded bg-white hover:bg-brand-purple hover:text-white border border-purple-100 transition-colors"
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Slug / URL personalizada */}
                <div className="md:col-span-2">
                  <Label htmlFor="custom_slug">URL personalizada</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500 whitespace-nowrap">inmopages.app/propiedades/</span>
                    <Input
                      id="custom_slug"
                      value={formData.custom_slug}
                      onChange={(e) => {
                        // Only allow valid slug characters
                        const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                        setFormData({ ...formData, custom_slug: value });
                      }}
                      placeholder={formData.title ? formData.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').slice(0, 50) : 'mi-propiedad'}
                      className="flex-1"
                      data-testid="custom-slug-input"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {isEdit ? "Cambia la URL de tu landing page. Se auto-actualiza si cambias el título." : "Déjalo vacío para generarlo automáticamente del título."}
                  </p>
                </div>

                <div>
                  <Label htmlFor="property_type">Tipo de Propiedad *</Label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casa">Casa</SelectItem>
                      <SelectItem value="departamento">Departamento</SelectItem>
                      <SelectItem value="terreno">Terreno</SelectItem>
                      <SelectItem value="local-comercial">Local Comercial</SelectItem>
                      <SelectItem value="oficina">Oficina</SelectItem>
                      <SelectItem value="bodega">Bodega</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="operation_type">Tipo de Operación *</Label>
                  <Select
                    value={formData.operation_type}
                    onValueChange={(value) => setFormData({ ...formData, operation_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="venta">Venta</SelectItem>
                      <SelectItem value="renta">Renta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="status">Estatus de la Propiedad *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Activa (Disponible)</SelectItem>
                      <SelectItem value="sold">Vendida</SelectItem>
                      <SelectItem value="rented">Rentada</SelectItem>
                      <SelectItem value="reserved">Reservada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="price">Precio (MXN) *</Label>
                  <Input
                    id="price"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="2500000"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="description">Descripción *</Label>
                    <button
                      type="button"
                      onClick={handleAiDescription}
                      disabled={aiLoading.description}
                      className="text-xs flex items-center gap-1 text-brand-purple hover:text-brand-pink disabled:opacity-50 font-medium"
                      data-testid="ai-description-btn"
                    >
                      {aiLoading.description ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isProPlus ? <Sparkles className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />)}
                      {aiLoading.description ? "Generando..." : "Generar con IA"}
                    </button>
                  </div>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción detallada de la propiedad..."
                    rows={4}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-brand-purple" />
                Ubicación
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Address Search */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-lg p-4">
                <Label htmlFor="address_search" className="flex items-center mb-2 font-semibold text-brand-purple">
                  <Search className="w-4 h-4 mr-2" />
                  Buscar Dirección
                </Label>
                <p className="text-sm text-gray-600 mb-3">
                  Escribe la dirección y selecciona de las opciones. Las coordenadas se llenarán automáticamente.
                </p>
                <div className="relative">
                  <Input
                    id="address_search"
                    data-testid="address-search-input"
                    value={addressSearch}
                    onChange={(e) => {
                      setAddressSearch(e.target.value);
                      setShowAddressResults(true);
                    }}
                    onFocus={() => addressResults.length > 0 && setShowAddressResults(true)}
                    placeholder="Ej: Av. Chapultepec 123, Guadalajara, Jalisco"
                    className="pr-10"
                  />
                  {searchingAddress && (
                    <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  )}
                  
                  {/* Search Results Dropdown */}
                  {showAddressResults && addressResults.length > 0 && (
                    <div 
                      className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                      data-testid="address-results-dropdown"
                    >
                      {addressResults.map((result, index) => (
                        <button
                          key={index}
                          type="button"
                          data-testid={`address-result-${index}`}
                          onClick={() => handleSelectAddress(result)}
                          className="w-full px-4 py-3 text-left hover:bg-purple-50 border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="flex items-start">
                            <MapPin className="w-4 h-4 text-brand-purple mt-1 mr-2 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{result.display_name.split(',').slice(0, 2).join(',')}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{result.display_name}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Coordinates Display */}
                {formData.latitude && formData.longitude && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-800">
                      <MapPin className="w-3 h-3 mr-1" />
                      Lat: {parseFloat(formData.latitude).toFixed(6)}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-100 text-green-800">
                      Lng: {parseFloat(formData.longitude).toFixed(6)}
                    </span>
                  </div>
                )}
              </div>

              {/* Close dropdown when clicking outside */}
              {showAddressResults && (
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowAddressResults(false)}
                />
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="location">Ciudad/Zona General *</Label>
                  <Input
                    id="location"
                    data-testid="location-input"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Guadalajara, Jalisco"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Ubicación general para mostrar públicamente</p>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="street">Calle</Label>
                  <Input
                    id="street"
                    data-testid="street-input"
                    value={formData.address.street}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value }
                    })}
                    placeholder="Av. Chapultepec"
                  />
                </div>

                <div>
                  <Label htmlFor="exterior_number">Número Exterior</Label>
                  <Input
                    id="exterior_number"
                    data-testid="exterior-number-input"
                    value={formData.address.exterior_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, exterior_number: e.target.value }
                    })}
                    placeholder="123"
                  />
                </div>

                <div>
                  <Label htmlFor="interior_number">Número Interior</Label>
                  <Input
                    id="interior_number"
                    data-testid="interior-number-input"
                    value={formData.address.interior_number}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, interior_number: e.target.value }
                    })}
                    placeholder="Apto 5B"
                  />
                </div>

                <div>
                  <Label htmlFor="postal_code">Código Postal</Label>
                  <Input
                    id="postal_code"
                    data-testid="postal-code-input"
                    value={formData.address.postal_code}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, postal_code: e.target.value }
                    })}
                    placeholder="44100"
                  />
                </div>

                <div>
                  <Label htmlFor="neighborhood">Colonia</Label>
                  <Input
                    id="neighborhood"
                    data-testid="neighborhood-input"
                    value={formData.address.neighborhood}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, neighborhood: e.target.value }
                    })}
                    placeholder="Americana"
                  />
                </div>

                <div>
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    data-testid="city-input"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                    placeholder="Guadalajara"
                  />
                </div>

                <div>
                  <Label htmlFor="state">Estado</Label>
                  <Input
                    id="state"
                    data-testid="state-input"
                    value={formData.address.state}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value }
                    })}
                    placeholder="Jalisco"
                  />
                </div>
              </div>

              {/* Map Preview */}
              {formData.latitude && formData.longitude && (
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-3 py-2 border-b flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 flex items-center">
                      <MapPin className="w-4 h-4 mr-1 text-brand-purple" />
                      Vista Previa del Mapa
                    </span>
                    <a 
                      href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-brand-purple hover:underline"
                    >
                      Ver en Google Maps →
                    </a>
                  </div>
                  <iframe
                    title="Map Preview"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    loading="lazy"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(formData.longitude) - 0.01},${parseFloat(formData.latitude) - 0.01},${parseFloat(formData.longitude) + 0.01},${parseFloat(formData.latitude) + 0.01}&layer=mapnik&marker=${formData.latitude},${formData.longitude}`}
                  />
                </div>
              )}

              {/* Manual Coordinates (Collapsed) */}
              <details className="group">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center">
                  <span className="mr-2">▶</span>
                  <span className="group-open:hidden">Ingresar coordenadas manualmente</span>
                  <span className="hidden group-open:inline">Ocultar coordenadas manuales</span>
                </summary>
                <div className="mt-3 grid md:grid-cols-2 gap-4 pl-5 border-l-2 border-gray-200">
                  <div>
                    <Label htmlFor="latitude">Latitud</Label>
                    <Input
                      id="latitude"
                      data-testid="latitude-input"
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      placeholder="20.6597"
                    />
                    <p className="text-xs text-gray-400 mt-1">Rango válido: -90 a 90</p>
                  </div>
                  <div>
                    <Label htmlFor="longitude">Longitud</Label>
                    <Input
                      id="longitude"
                      data-testid="longitude-input"
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      placeholder="-103.3496"
                    />
                    <p className="text-xs text-gray-400 mt-1">Rango válido: -180 a 180</p>
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>

          {/* Specs */}
          <Card>
            <CardHeader>
              <CardTitle>Especificaciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="bedrooms">Recámaras</Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    value={formData.specs.bedrooms}
                    onChange={(e) => setFormData({
                      ...formData,
                      specs: { ...formData.specs, bedrooms: e.target.value }
                    })}
                    placeholder="3"
                  />
                </div>

                <div>
                  <Label htmlFor="bathrooms">Baños Completos</Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    value={formData.specs.bathrooms}
                    onChange={(e) => setFormData({
                      ...formData,
                      specs: { ...formData.specs, bathrooms: e.target.value }
                    })}
                    placeholder="2"
                  />
                </div>

                <div>
                  <Label htmlFor="half_bathrooms">Medios Baños</Label>
                  <Input
                    id="half_bathrooms"
                    type="number"
                    value={formData.specs.half_bathrooms}
                    onChange={(e) => setFormData({
                      ...formData,
                      specs: { ...formData.specs, half_bathrooms: e.target.value }
                    })}
                    placeholder="1"
                  />
                </div>

                <div>
                  <Label htmlFor="parking">Estacionamientos</Label>
                  <Input
                    id="parking"
                    type="number"
                    value={formData.specs.parking}
                    onChange={(e) => setFormData({
                      ...formData,
                      specs: { ...formData.specs, parking: e.target.value }
                    })}
                    placeholder="2"
                  />
                </div>

                <div>
                  <Label htmlFor="square_meters">m² Terreno</Label>
                  <Input
                    id="square_meters"
                    type="number"
                    step="0.01"
                    value={formData.specs.square_meters}
                    onChange={(e) => setFormData({
                      ...formData,
                      specs: { ...formData.specs, square_meters: e.target.value }
                    })}
                    placeholder="250"
                  />
                </div>

                <div>
                  <Label htmlFor="construction_meters">m² Construcción</Label>
                  <Input
                    id="construction_meters"
                    type="number"
                    step="0.01"
                    value={formData.specs.construction_meters}
                    onChange={(e) => setFormData({
                      ...formData,
                      specs: { ...formData.specs, construction_meters: e.target.value }
                    })}
                    placeholder="180"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-3 block">Espacios Adicionales</Label>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="tv_room"
                      checked={formData.specs.tv_room}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        specs: { ...formData.specs, tv_room: checked }
                      })}
                    />
                    <Label htmlFor="tv_room" className="font-normal cursor-pointer">
                      Estar de TV
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="laundry_room"
                      checked={formData.specs.laundry_room}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        specs: { ...formData.specs, laundry_room: checked }
                      })}
                    />
                    <Label htmlFor="laundry_room" className="font-normal cursor-pointer">
                      Cuarto de Lavado
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="service_room"
                      checked={formData.specs.service_room}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        specs: { ...formData.specs, service_room: checked }
                      })}
                    />
                    <Label htmlFor="service_room" className="font-normal cursor-pointer">
                      Cuarto de Servicio
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="roof_garden"
                      checked={formData.specs.roof_garden}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        specs: { ...formData.specs, roof_garden: checked }
                      })}
                    />
                    <Label htmlFor="roof_garden" className="font-normal cursor-pointer">
                      Roof Garden
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="balcony"
                      checked={formData.specs.balcony}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        specs: { ...formData.specs, balcony: checked }
                      })}
                    />
                    <Label htmlFor="balcony" className="font-normal cursor-pointer">
                      Balcón
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="terrace"
                      checked={formData.specs.terrace}
                      onCheckedChange={(checked) => setFormData({
                        ...formData,
                        specs: { ...formData.specs, terrace: checked }
                      })}
                    />
                    <Label htmlFor="terrace" className="font-normal cursor-pointer">
                      Terraza
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Amenities */}
          <Card>
            <CardHeader>
              <CardTitle>Amenidades</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <Input
                  value={newAmenity}
                  onChange={(e) => setNewAmenity(e.target.value)}
                  placeholder="Ej: Alberca, Jardín, Gym..."
                  onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addAmenity())}
                />
                <Button type="button" onClick={addAmenity}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.amenities && formData.amenities.length > 0 ? (
                  formData.amenities.map((amenity, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-2 bg-purple-100 text-brand-purple px-3 py-1 rounded-full"
                    >
                      <span>{typeof amenity === 'string' ? amenity : amenity.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No hay amenidades agregadas</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Images */}
          <Card>
            <CardHeader>
              <CardTitle>Imágenes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Main Image */}
              <div>
                <Label htmlFor="main_image" className="text-lg font-semibold mb-2 block">
                  Imagen Principal * {!isEdit && "(Recomendada)"}
                </Label>
                <p className="text-sm text-gray-500 mb-2">
                  Esta será la imagen destacada de la propiedad
                </p>
                <Input
                  id="main_image"
                  type="file"
                  accept="image/*"
                  onChange={isEdit ? handleMainImageUpload : handleMainImageSelect}
                  disabled={uploadingImages}
                />
                
                {/* Preview main image for new property */}
                {!isEdit && mainImagePreview && (
                  <div className="mt-4">
                    <div className="relative inline-block">
                      <img
                        src={mainImagePreview}
                        alt="Vista previa imagen principal"
                        className="w-64 h-48 object-cover rounded-lg border-2 border-brand-purple"
                      />
                      <button
                        type="button"
                        onClick={removeMainImage}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Display current main image for edit mode */}
                {isEdit && currentProperty?.main_image && (
                  <div className="mt-4">
                    <Label className="text-sm text-gray-500 mb-2 block">Imagen Principal Actual</Label>
                    <div className="relative inline-block">
                      <img
                        src={getImageUrl(currentProperty.main_image)}
                        alt="Imagen principal"
                        className="w-64 h-48 object-cover rounded-lg border-2 border-brand-purple"
                      />
                      <button
                        type="button"
                        onClick={handleDeleteMainImage}
                        className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                        title="Eliminar imagen principal"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-6">
                <Label htmlFor="gallery_images" className="text-lg font-semibold mb-2 block">
                  Galería de Fotos (Máximo 10 fotos)
                </Label>
                <p className="text-sm text-gray-500 mb-2">
                  Sube múltiples imágenes para mostrar más detalles de la propiedad
                </p>
                <Input
                  id="gallery_images"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={isEdit ? handleImageUpload : handleImageSelect}
                  disabled={uploadingImages}
                />
                {uploadingImages && <p className="text-sm text-gray-500 mt-2">Subiendo...</p>}
                {!isEdit && selectedImages.length > 0 && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ {selectedImages.length} imagen(es) seleccionada(s) para galería
                  </p>
                )}
                {isEdit && currentProperty?.images && (
                  <p className="text-sm text-gray-500 mt-2">
                    {currentProperty.images.length}/10 imágenes en galería
                  </p>
                )}
              </div>

              {/* Preview gallery for new property */}
              {!isEdit && imagePreview.length > 0 && (
                <div>
                  <Label className="mb-2 block">Vista Previa Galería</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {imagePreview.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeSelectedImage(index)}
                          className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery for edit mode */}
              {isEdit && currentProperty?.images && currentProperty.images.length > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Galería Actual</Label>
                    <span className="text-sm font-semibold text-brand-purple">
                      {currentProperty.images.length} / 10 fotos
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {currentProperty.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={getImageUrl(image)}
                          alt={`Galería ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            console.error("Gallery image failed:", e.target.src);
                            e.target.style.display = 'none';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(image)}
                          className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact & Video */}
          <Card>
            <CardHeader>
              <CardTitle>Contacto y Multimedia</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="whatsapp_number">WhatsApp *</Label>
                  <Input
                    id="whatsapp_number"
                    value={formData.whatsapp_number}
                    onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                    placeholder="523332509484"
                    required
                    data-testid="whatsapp-input"
                  />
                </div>

                <div>
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contacto@ejemplo.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Método de contacto en la landing</Label>
                  <p className="text-xs text-gray-500 mb-2">Elige cómo quieres que te contacten los interesados. Esto te permite optimizar tus campañas por tipo de evento.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { value: "both", label: "Ambos", desc: "WhatsApp + Formulario" },
                      { value: "whatsapp", label: "Solo WhatsApp", desc: "Solo botón de WhatsApp" },
                      { value: "form", label: "Solo Formulario", desc: "Solo formulario de contacto" }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, contact_method: option.value })}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          formData.contact_method === option.value
                            ? 'border-brand-purple bg-brand-purple/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`contact-method-${option.value}`}
                      >
                        <p className="font-medium text-sm">{option.label}</p>
                        <p className="text-xs text-gray-500">{option.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom WhatsApp message */}
                {formData.contact_method !== "form" && (
                  <div className="md:col-span-2 p-4 rounded-lg bg-green-50/50 border border-green-100">
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="whatsapp_message" className="flex items-center gap-1.5">
                        <span className="text-green-600">●</span> Mensaje pre-cargado en WhatsApp
                      </Label>
                      <button
                        type="button"
                        onClick={handleAiWhatsapp}
                        disabled={aiLoading.whatsapp}
                        className="text-xs flex items-center gap-1 text-brand-purple hover:text-brand-pink disabled:opacity-50 font-medium"
                        data-testid="ai-whatsapp-btn"
                      >
                        {aiLoading.whatsapp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isProPlus ? <Sparkles className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />)}
                        {aiLoading.whatsapp ? "Generando..." : "Generar con IA"}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      Texto que se rellena automáticamente cuando el visitante toca el botón de WhatsApp. Usa <code className="bg-white px-1 rounded text-[11px]">{"{title}"}</code> para insertar el título de la propiedad.
                    </p>
                    <textarea
                      id="whatsapp_message"
                      value={formData.whatsapp_message}
                      onChange={(e) => setFormData({ ...formData, whatsapp_message: e.target.value })}
                      placeholder="Hola, estoy interesado en la propiedad: {title}"
                      maxLength={500}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple/30 focus:border-brand-purple resize-none"
                      data-testid="whatsapp-message-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      {formData.whatsapp_message?.length || 0}/500 · Si lo dejas vacío usaremos el mensaje predeterminado.
                    </p>
                  </div>
                )}

                {/* Custom form title and description */}
                {formData.contact_method !== "whatsapp" && (
                  <div className="md:col-span-2 p-4 rounded-lg bg-purple-50/50 border border-purple-100 space-y-3">
                    <div>
                      <Label htmlFor="form_title" className="flex items-center gap-1.5">
                        <span className="text-brand-purple">●</span> Título del formulario
                      </Label>
                      <p className="text-xs text-gray-500 mb-2">Cabecera del formulario de contacto. Si lo dejas vacío usaremos "¿Te interesa?"</p>
                      <Input
                        id="form_title"
                        value={formData.form_title}
                        onChange={(e) => setFormData({ ...formData, form_title: e.target.value })}
                        placeholder="¿Te interesa?"
                        maxLength={80}
                        data-testid="form-title-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="form_description">Descripción del formulario</Label>
                      <p className="text-xs text-gray-500 mb-2">Texto debajo del título. Si lo dejas vacío usaremos "Déjanos tus datos y te contactamos".</p>
                      <textarea
                        id="form_description"
                        value={formData.form_description}
                        onChange={(e) => setFormData({ ...formData, form_description: e.target.value })}
                        placeholder="Déjanos tus datos y te contactamos en menos de 24 horas"
                        maxLength={200}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple/30 focus:border-brand-purple resize-none"
                        data-testid="form-description-input"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        {formData.form_description?.length || 0}/200
                      </p>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <Label htmlFor="video_url">Video (YouTube/Vimeo URL)</Label>
                  <Input
                    id="video_url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>


          {/* Template Selector */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Layout className="w-5 h-5 mr-2 text-brand-purple" />
                Plantilla de Landing
              </CardTitle>
              <p className="text-sm text-gray-500">Elige el diseño de tu landing page {userPlan === 'basico' && <span className="text-amber-600 font-medium">— Disponible desde plan Pro</span>}</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { id: "clasica", name: "Clásica", desc: "Hero grande con galería e info debajo", color: "from-purple-500 to-pink-500" },
                  { id: "moderna", name: "Moderna", desc: "Galería izquierda, info y formulario derecha", color: "from-blue-500 to-cyan-500" },
                  { id: "elegante", name: "Elegante", desc: "Fondo oscuro, hero fullscreen, look premium", color: "from-gray-800 to-gray-600" }
                ].map(tmpl => {
                  const isLocked = userPlan === 'basico' && tmpl.id !== 'clasica';
                  return (
                    <button
                      key={tmpl.id}
                      type="button"
                      disabled={isLocked}
                      onClick={() => !isLocked && setFormData({...formData, template: tmpl.id})}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        formData.template === tmpl.id 
                          ? 'border-brand-purple shadow-md bg-purple-50' 
                          : isLocked 
                            ? 'border-gray-200 opacity-50 cursor-not-allowed'
                            : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                      }`}
                      data-testid={`template-${tmpl.id}`}
                    >
                      <div className={`w-full h-20 rounded-lg bg-gradient-to-br ${tmpl.color} mb-3 ${isLocked ? 'grayscale' : ''}`}>
                        {tmpl.id === "clasica" && (
                          <div className="w-full h-full flex flex-col p-2 gap-1">
                            <div className="h-1/2 bg-white/30 rounded" />
                            <div className="flex gap-1 h-1/2">
                              <div className="w-1/2 bg-white/20 rounded" />
                              <div className="w-1/2 bg-white/20 rounded" />
                            </div>
                          </div>
                        )}
                        {tmpl.id === "moderna" && (
                          <div className="w-full h-full flex p-2 gap-1">
                            <div className="w-3/5 bg-white/30 rounded" />
                            <div className="w-2/5 flex flex-col gap-1">
                              <div className="h-1/3 bg-white/20 rounded" />
                              <div className="h-2/3 bg-white/20 rounded" />
                            </div>
                          </div>
                        )}
                        {tmpl.id === "elegante" && (
                          <div className="w-full h-full flex flex-col justify-end p-2">
                            <div className="h-1 bg-white/40 rounded w-3/4 mb-1" />
                            <div className="h-1 bg-white/20 rounded w-1/2" />
                          </div>
                        )}
                      </div>
                      <p className="font-semibold text-sm text-gray-900">{tmpl.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{tmpl.desc}</p>
                      {isLocked && (
                        <div className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">
                          Pro+
                        </div>
                      )}
                      {formData.template === tmpl.id && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-brand-purple rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Custom Colors */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <span className="w-6 h-6 rounded-full bg-gradient-to-r from-brand-purple to-brand-pink mr-2"></span>
                Colores de la Landing
              </CardTitle>
              <p className="text-sm text-gray-500">Personaliza los colores de tu landing page</p>
            </CardHeader>
            <CardContent>
              {/* Presets */}
              <div className="mb-6">
                <Label className="mb-3 block">Paletas predefinidas</Label>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                  {colorPresets.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        primary_color: preset.primary,
                        secondary_color: preset.secondary
                      })}
                      className={`p-2 rounded-lg border-2 transition-all ${
                        formData.primary_color === preset.primary 
                          ? 'border-gray-900 shadow-lg scale-105' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      title={preset.name}
                    >
                      <div className="flex gap-1">
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: preset.primary }}
                        />
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: preset.secondary }}
                        />
                      </div>
                      <p className="text-xs mt-1 text-center">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Colors */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="primary_color" className="mb-2 block">Color Primario</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="primary_color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      placeholder="#a033fd"
                      className="font-mono"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="secondary_color" className="mb-2 block">Color Secundario</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id="secondary_color"
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    />
                    <Input
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      placeholder="#ff1758"
                      className="font-mono"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 border rounded-lg bg-gray-50">
                <Label className="mb-3 block">Vista previa</Label>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: formData.primary_color }}
                  >
                    Botón Primario
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: formData.secondary_color }}
                  >
                    Botón Secundario
                  </button>
                  <span 
                    className="px-3 py-1 rounded-full text-white text-sm font-semibold"
                    style={{ backgroundColor: formData.primary_color }}
                  >
                    CASA EN VENTA
                  </span>
                  <span 
                    className="font-bold text-xl"
                    style={{ color: formData.primary_color }}
                  >
                    $1,500,000
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tracking Pixels */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                Pixels de Seguimiento
              </CardTitle>
              <p className="text-sm text-gray-500">Configura tus pixels para tracking de conversiones y remarketing</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Facebook Pixel */}
              <div>
                <Label htmlFor="facebook_pixel" className="flex items-center mb-2">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook Pixel ID
                </Label>
                <Input
                  id="facebook_pixel"
                  value={formData.facebook_pixel_id}
                  onChange={(e) => setFormData({ ...formData, facebook_pixel_id: e.target.value })}
                  placeholder="123456789012345"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Encuéntralo en Facebook Events Manager → Orígenes de datos → Tu Pixel
                </p>
              </div>

              {/* Google Analytics */}
              <div>
                <Label htmlFor="google_analytics" className="flex items-center mb-2">
                  <svg className="w-5 h-5 mr-2 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.84 2.998v17.998c-.004 1.66-1.346 3.004-3.002 3.004H4.162A3.006 3.006 0 011.16 20.996V2.998A3.006 3.006 0 014.162 0h15.676c1.656 0 2.998 1.342 3.002 2.998zM19.5 18.002a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0zM12 12.002a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm-4.5.75a2.25 2.25 0 10-4.5 0v5.25a2.25 2.25 0 004.5 0v-5.25z"/>
                  </svg>
                  Google Analytics 4 (GA4)
                </Label>
                <Input
                  id="google_analytics"
                  value={formData.google_analytics_id}
                  onChange={(e) => setFormData({ ...formData, google_analytics_id: e.target.value })}
                  placeholder="G-XXXXXXXXXX"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Encuéntralo en Google Analytics → Admin → Flujos de datos → Tu flujo web
                </p>
              </div>

              {/* Google Ads */}
              <div>
                <Label htmlFor="google_ads" className="flex items-center mb-2">
                  <svg className="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
                  </svg>
                  Google Ads Conversion ID
                </Label>
                <Input
                  id="google_ads"
                  value={formData.google_ads_id}
                  onChange={(e) => setFormData({ ...formData, google_ads_id: e.target.value })}
                  placeholder="AW-XXXXXXXXXX"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Encuéntralo en Google Ads → Herramientas → Conversiones → Tu conversión
                </p>
              </div>

              {/* TikTok Pixel */}
              <div>
                <Label htmlFor="tiktok_pixel" className="flex items-center mb-2">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                  TikTok Pixel ID
                </Label>
                <Input
                  id="tiktok_pixel"
                  value={formData.tiktok_pixel_id}
                  onChange={(e) => setFormData({ ...formData, tiktok_pixel_id: e.target.value })}
                  placeholder="XXXXXXXXXXXXXXXXXX"
                  className="font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Encuéntralo en TikTok Ads Manager → Activos → Eventos → Gestionar
                </p>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> Los pixels se cargarán automáticamente en tu landing page. 
                  Los eventos de conversión se disparan cuando alguien envía el formulario de contacto o hace clic en WhatsApp.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pb-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/dashboard")}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-brand-purple hover:bg-brand-pink text-white"
              disabled={loading}
            >
              {loading ? "Guardando..." : isEdit ? "Actualizar Propiedad" : "Crear Propiedad"}
            </Button>
          </div>
        </form>
      </div>

      {/* AI Upsell Modal */}
      {aiUpsellOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAiUpsellOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="ai-upsell-modal">
            <div className="bg-gradient-to-br from-brand-purple to-brand-pink p-6 text-white text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-1">Redactor con IA</h3>
              <p className="text-white/85 text-sm">Disponible desde el plan Pro</p>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Genera descripciones, títulos y mensajes profesionales en segundos con inteligencia artificial.
              </p>
              <ul className="space-y-2 mb-5 text-sm">
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-purple" /> Descripciones persuasivas optimizadas para SEO</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-purple" /> 5 ideas de título por propiedad</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-purple" /> Mensajes de WhatsApp persuasivos</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand-purple" /> Modelo Gemini 3 Flash de Google</li>
              </ul>
              <Button
                onClick={() => navigate("/precios")}
                className="w-full bg-gradient-to-r from-brand-purple to-brand-pink text-white h-12 text-base font-semibold"
                data-testid="ai-upsell-upgrade-btn"
              >
                Mejorar mi plan
              </Button>
              <button onClick={() => setAiUpsellOpen(false)} className="block mx-auto mt-3 text-sm text-gray-500 hover:text-gray-700">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
